import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { BookOpen, Download, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const EBOOK_PDF_URL = "/downloads/O_Codigo_da_Riqueza.pdf";

export default function QuizCompletionBanner() {
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);

  const triggerDownload = () => {
    const link = document.createElement("a");
    link.href = EBOOK_PDF_URL;
    link.download = "O_Codigo_da_Riqueza_com_Consorcio.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Download do E-book iniciado!");
  };

  useEffect(() => {
    if (searchParams.get("concluiu_quiz") === "1") {
      setIsOpen(true);
      toast.success("Obrigado por responder ao Quiz! Seu e-book gratuito está disponível para download.", {
        duration: 8000,
        action: {
          label: "Baixar PDF",
          onClick: triggerDownload
        }
      });

      // Remove o parâmetro da URL suavemente
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("concluiu_quiz");
      window.history.replaceState({}, "", `${window.location.pathname}${newParams.toString() ? `?${newParams.toString()}` : ""}`);
    }
  }, [searchParams]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.95 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="fixed bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-lg z-50 pointer-events-auto"
      >
        <div className="bg-card-gradient border border-primary/50 shadow-gold rounded-2xl p-5 sm:p-6 relative backdrop-blur-xl overflow-hidden">
          {/* Fundo com gradiente sutil */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/15 rounded-full blur-2xl -z-10 pointer-events-none" />

          {/* Botão fechar */}
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground p-1.5 rounded-full hover:bg-white/5 transition-colors cursor-pointer"
            aria-label="Fechar aviso"
          >
            <X size={16} />
          </button>

          <div className="flex items-start gap-4">
            {/* Capa do E-book em miniatura */}
            <div className="w-14 h-18 rounded-lg overflow-hidden border border-primary/30 shadow-md shrink-0 bg-black/40 hidden sm:block">
              <img
                src="/images/ebook-cover.png"
                alt="Capa E-book"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>

            <div className="flex-1 pr-4">
              <div className="inline-flex items-center gap-1.5 bg-primary/15 text-primary text-[10px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full mb-1.5">
                <Sparkles size={11} className="text-primary animate-pulse" />
                <span>Quiz Concluído · Cortesia</span>
              </div>

              <h4 className="font-display text-base sm:text-lg font-medium text-foreground leading-snug mb-1">
                Seu E-book Gratuito está liberado!
              </h4>

              <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                Como agradecimento pela sua participação no Quiz, baixe o guia <strong className="text-foreground font-medium">O Código da Riqueza com Consórcio</strong> de Juliano Wagner.
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={triggerDownload}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-xs transition-all shadow-gold hover:translate-y-[-1px] cursor-pointer"
                  style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
                >
                  <Download size={14} /> Baixar E-book Agora (PDF)
                </button>

                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-3 py-2 transition-colors cursor-pointer"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
